import {
  Users,
  UsersRound,
  ShieldCheck,
  Cpu,
  Bot,
  CalendarClock,
  AppWindow,
  FileText,
  Settings2,
  Monitor,
  Zap,
  ScrollText,
  UserRound,
} from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import SettingsLandingCard from "../../components/Settings/SettingsLandingCard";
import UIStore from "../../store/uiStore";
import ROUTES from "../../router/routes";

// ─── Section group ─────────────────────────────────────────────────────────────
const SectionGroup = ({ title, children }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-widest text-ink-gray-500 dark:text-ink-gray-400 mb-2.5 px-0.5">
      {title}
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 [&>*:only-child]:col-span-2">{children}</div>
  </div>
);

// ─── Page ──────────────────────────────────────────────────────────────────────
const SettingsLandingPage = () => {
  const orgAvailable = UIStore((state) => state.orgAvailable);
  const isAdmin = orgAvailable === true;

  return (
    <SettingsPageShell
      title="Settings"
      description="Manage your workspace, identity, integrations, and platform configuration."
      maxWidth="4xl"
      onBack={null}
    >
      <div className="flex flex-col gap-7">

        <SectionGroup title="You">
          <SettingsLandingCard
            icon={UserRound}
            title="Profile"
            description="Identity, language, appearance, and response styles"
            href={ROUTES.SETTINGS.PROFILE}
            iconBg="from-gradient-teal-600 to-gradient-green-500"
          />
        </SectionGroup>

        {/* People — admin only */}
        {isAdmin && (
          <SectionGroup title="People">
            <SettingsLandingCard
              icon={Users}
              title="Users"
              description="Manage members, roles, and invitations"
              href={ROUTES.SETTINGS.USERS}
            />
            <SettingsLandingCard
              icon={UsersRound}
              title="Groups"
              description="Organise users into groups for access control"
              href={ROUTES.SETTINGS.GROUPS}
            />
          </SectionGroup>
        )}

        {/* Identity — admin only */}
        {isAdmin && (
          <SectionGroup title="Identity">
            <SettingsLandingCard
              icon={ShieldCheck}
              title="Identity & SSO"
              description="SSO providers, Google Workspace, and SCIM provisioning"
              href={ROUTES.SETTINGS.IDENTITY}
              iconBg="from-gradient-violet-600 to-gradient-accent"
            />
          </SectionGroup>
        )}

        {/* Governance & Finance — admin only */}
        {isAdmin && (
          <SectionGroup title="Governance &amp; Finance">
            <SettingsLandingCard
              icon={Zap}
              title="FinOps"
              description="Unit consumption, usage forecasts, and per-user spend breakdown"
              href={ROUTES.SETTINGS.FINOPS}
              iconBg="from-gradient-amber-500 to-gradient-amber-400"
            />
            <SettingsLandingCard
              icon={ScrollText}
              title="Audit Log"
              description="Immutable record of admin, collaboration, and platform events"
              href={ROUTES.SETTINGS.AUDIT_LOG}
              iconBg="from-gradient-gray-500 to-gradient-gray-400"
            />
          </SectionGroup>
        )}

        {/* AI & Models — all users */}
        <SectionGroup title="AI &amp; Models">
          <SettingsLandingCard
            icon={Cpu}
            title="Model Garden"
            description="Browse and configure AI models available in your workspace"
            href={ROUTES.SETTINGS.MODELS}
          />
          {isAdmin && (
            <SettingsLandingCard
              icon={Bot}
              title="Forge Providers"
              description="Register OAuth providers and secure their credentials"
              href={ROUTES.SETTINGS.FORGE_PROVIDERS}
            />
          )}
        </SectionGroup>

        {/* Automation — all users */}
        <SectionGroup title="Automation">
          <SettingsLandingCard
            icon={CalendarClock}
            title="Scheduled Jobs"
            description="Recurring prompt schedules for your workspace"
            href={ROUTES.SETTINGS.SCHEDULED_JOBS}
            iconBg="from-gradient-violet-600 to-gradient-violet-400"
          />
        </SectionGroup>

        {/* Edge — all users */}
        <SectionGroup title="Edge">
          <SettingsLandingCard
            icon={Monitor}
            title="Desktop"
            description="Connect and manage machines running Photon Desktop"
            href={ROUTES.SETTINGS.DESKTOP}
            iconBg="from-gradient-ink to-gradient-primary"
          />
        </SectionGroup>

        {/* Developer — admin only */}
        {isAdmin && (
          <SectionGroup title="Developer">
            <SettingsLandingCard
              icon={AppWindow}
              title="Client Apps"
              description="OAuth clients and API access for external integrations"
              href={ROUTES.SETTINGS.APPS}
              iconBg="from-gradient-ink to-gradient-primary"
            />
          </SectionGroup>
        )}

        {/* Compliance — admin only */}
        {isAdmin && (
          <SectionGroup title="Compliance">
            <SettingsLandingCard
              icon={FileText}
              title="Audit Exports"
              description="Export activity logs for compliance and review"
              href={ROUTES.SETTINGS.AUDIT_EXPORTS}
              iconBg="from-gradient-hex-059669 to-gradient-teal-600"
            />
          </SectionGroup>
        )}

        {/* Configuration — admin only (Phase 2) */}
        {isAdmin && (
          <SectionGroup title="Configuration">
            <SettingsLandingCard
              icon={Settings2}
              title="Platform Config"
              description="Email, scheduler, integrations, and service configuration"
              href={ROUTES.SETTINGS.CONFIG}
              iconBg="from-gradient-gray-500 to-gradient-gray-700"
            />
          </SectionGroup>
        )}

      </div>
    </SettingsPageShell>
  );
};

export default SettingsLandingPage;
